import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import IncomeChooser from '../components/IncomeChooser';
import { ChipSelect, Confetti, Field, Logo, Page, Petals, Select } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api, setToken } from '../utils/api';
import { HEIGHTS, toDateInput, withOption } from '../utils/format';

function AuthAside({ title, text }) {
  return (
    <aside className="auth__aside">
      <Petals count={10} />
      <Logo light />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <h2>{title}</h2>
        <p>{text}</p>
      </motion.div>
      <div className="auth__quote">
        <p>“We were both on night duty when we first chatted on CTshaadi. Two residencies and one wedding later, we still share our coffee breaks!”</p>
        <small>— Dr. Nidhi & Dr. Varun, married 2026</small>
      </div>
    </aside>
  );
}

function MedhireForm({ onResult, cta = 'Continue with MedHire' }) {
  const { toast } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      onResult(await api('/user/medhire/login', { method: 'POST', body: { email, password } }));
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="stack">
      <div className="medhire-note">
        <span className="medhire-chip"><Icon name="steth" size={13} /> MedHire</span>
        <p>Use your MedHire email and password. We only <strong>read</strong> your MedHire details to fill your profile — nothing on MedHire is changed.</p>
      </div>
      <Field label="MedHire email">
        <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@hospital.com" autoComplete="username" />
      </Field>
      <Field label="MedHire password">
        <input className="input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
      </Field>
      <motion.button className="btn btn--teal btn--block btn--lg" whileTap={{ scale: 0.97 }} disabled={busy}>
        {busy ? 'Checking MedHire…' : cta}
        {!busy && <Icon name="arrowRight" size={18} />}
      </motion.button>
    </form>
  );
}

export function Login() {
  const { startSession, toast } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const next = params.get('next') || '/home';

  const done = (s) => {
    startSession(s);
    toast(`Welcome back, ${s.user.firstName} 👋`, 'success');
    navigate(next, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      done(await api('/user/login', { method: 'POST', body: { email, password } }));
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const onMedhire = (r) => {
    if (r.token) return done(r);
    // New to CTshaadi: carry the verified MedHire data into registration.
    return navigate('/register', { state: { medhire: r } });
  };

  return (
    <Page className="auth">
      <AuthAside title="Welcome back" text="Your matches have been waiting. Pick up right where you left off." />
      <section className="auth__main">
        <motion.div className="auth__card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1>Login to CTshaadi</h1>
          <p className="muted">New here? <Link to="/register">Create a free profile</Link></p>
          <div className="seg seg--wide">
            {[['email', 'Email / Phone'], ['medhire', 'MedHire account']].map(([k, l]) => (
              <button key={k} type="button" className={mode === k ? 'is-active' : ''} onClick={() => setMode(k)}>
                {mode === k && <motion.span layoutId="login-seg" className="seg__pill" />}
                <span>{l}</span>
              </button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={mode} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              {mode === 'email' ? (
                <form onSubmit={submit} className="stack">
                  <Field label="Email, phone or profile ID">
                    <input className="input" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="username" />
                  </Field>
                  <Field label="Password">
                    <input className="input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
                  </Field>
                  <motion.button className="btn btn--primary btn--block btn--lg" whileTap={{ scale: 0.97 }} disabled={busy}>
                    {busy ? 'Logging in…' : 'Login'}
                  </motion.button>
                  <button
                    type="button"
                    className="demo-hint"
                    onClick={() => {
                      setEmail('arjun@demo.ctshaadi.com');
                      setPassword('demo123');
                    }}
                  >
                    <Icon name="sparkle" size={14} /> Try the demo account
                  </button>
                </form>
              ) : (
                <MedhireForm onResult={onMedhire} />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </section>
    </Page>
  );
}

const STEPS = [
  { key: 'basics', title: 'The basics', icon: 'user' },
  { key: 'personal', title: 'Personal details', icon: 'heart' },
  { key: 'career', title: 'Education & career', icon: 'cap' },
  { key: 'about', title: 'About & photos', icon: 'camera' },
];

const IMPORTED_LABELS = [
  ['firstName', 'Name'], ['dob', 'Date of birth'], ['gender', 'Gender'], ['maritalStatus', 'Marital status'], ['city', 'City'],
  ['languages', 'Languages'], ['specialty', 'Specialty'], ['education', 'Education'], ['workHistory', 'Work history'],
  ['experienceYears', 'Experience'], ['photos', 'Profile photo'], ['about', 'About you'],
];
const filled = (v) => (Array.isArray(v) ? v.length > 0 : v !== '' && v !== null && v !== undefined && v !== 0);

export function Register() {
  const { meta, startSession, toast } = useApp();
  const o = meta?.options || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const fromLogin = location.state?.medhire;

  const [method, setMethod] = useState(fromLogin ? 'medhire' : params.get('medhire') ? 'medhire-form' : null);
  const [step, setStep] = useState(fromLogin ? -1 : 0);
  const [ticket, setTicket] = useState(fromLogin?.ticket || null);
  const [medhireSalary, setMedhireSalary] = useState(fromLogin?.prefill?.medhireSalary || null);
  const [prefilled, setPrefilled] = useState(() => (fromLogin ? Object.keys(fromLogin.prefill).filter((k) => filled(fromLogin.prefill[k])) : []));
  const [form, setForm] = useState(() => ({
    createdBy: 'Self',
    gender: location.state?.gender || '',
    maritalStatus: 'Never Married',
    country: 'India',
    hobbies: [],
    income: { source: 'custom', period: 'yearly', visibility: 'all' },
    ...(fromLogin ? fromPrefill(fromLogin.prefill) : {}),
  }));
  const [password, setPassword] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setEdu = (k) => (e) => setForm((f) => ({ ...f, education: [{ ...(f.education?.[0] || {}), [k]: e.target.value }, ...(f.education || []).slice(1)] }));
  const tag = (k) => (prefilled.includes(k) ? 'from MedHire' : null);

  function fromPrefill(p) {
    const { medhireSalary: s, ...rest } = p;
    return {
      ...rest,
      dob: toDateInput(rest.dob),
      income: { source: s?.present || s?.expected ? 'medhire' : 'custom', period: 'monthly', visibility: 'all' },
    };
  }

  const onMedhire = (r) => {
    if (r.token) {
      startSession(r);
      toast('This MedHire account already has a CTshaadi profile — welcome back!', 'success');
      return navigate('/home');
    }
    setTicket(r.ticket);
    setMedhireSalary(r.prefill.medhireSalary);
    setPrefilled(Object.keys(r.prefill).filter((k) => filled(r.prefill[k])));
    setForm((f) => ({ ...f, ...fromPrefill(r.prefill) }));
    setMethod('medhire');
    return setStep(-1);
  };

  const age = form.dob ? Math.floor((Date.now() - new Date(form.dob)) / 31557600000) : null;
  const errors = useMemo(() => {
    const e = {};
    if (step === 0) {
      if (!form.firstName) e.firstName = 'Required';
      if (!form.gender) e.gender = 'Required';
      if (!form.dob) e.dob = 'Required';
      else if (age < 18) e.dob = 'You must be at least 18';
      if (!/^\S+@\S+\.\S+$/.test(form.email || '')) e.email = 'Enter a valid email';
      if (!ticket && password.length < 6) e.password = 'At least 6 characters';
    }
    if (step === 1) {
      if (!form.religion) e.religion = 'Required';
      if (!form.motherTongue) e.motherTongue = 'Required';
      if (!form.city) e.city = 'Required';
      if (!form.heightCm) e.heightCm = 'Required';
    }
    if (step === 2) {
      if (!form.highestQualification) e.highestQualification = 'Required';
      if (!form.profession) e.profession = 'Required';
    }
    return e;
  }, [step, form, password, ticket, age]);
  const [showErrors, setShowErrors] = useState(false);
  const err = (k) => showErrors && errors[k] && <span className="field__error">{errors[k]}</span>;

  const next = () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      return toast('Please complete the highlighted fields', 'error');
    }
    setShowErrors(false);
    if (step < STEPS.length - 1) return setStep(step + 1);
    return submit();
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...form, heightCm: Number(form.heightCm) || undefined, experienceYears: Number(form.experienceYears) || 0, medhireTicket: ticket || undefined, password: password || undefined };
      const session = await api('/user/register', { method: 'POST', body });
      // Store the token for the photo upload, but start the session only after
      // the celebration so the guest-only route doesn't redirect mid-animation.
      setToken(session.token);
      let { user: created } = session;
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append('photos', f));
        await api('/user/me/photos', { method: 'POST', form: fd })
          .then((r) => {
            created = r.user;
          })
          .catch(() => toast('Photo upload failed — you can add photos from your profile', 'error'));
      }
      setCelebrate(true);
      setTimeout(() => {
        startSession({ token: session.token, user: created });
        navigate('/home', { replace: true });
      }, 1900);
    } catch (e) {
      toast(e.message, 'error');
      setBusy(false);
    }
  };

  const imported = IMPORTED_LABELS.filter(([k]) => prefilled.includes(k));

  return (
    <Page className="auth">
      <AuthAside
        title={method === 'medhire' ? 'Imported from MedHire ✨' : 'Begin your love story'}
        text={method === 'medhire' ? 'Review what we brought over. Edit anything — your MedHire account stays exactly as it is.' : 'It takes about 3 minutes. Your details stay private until you choose to share them.'}
      />
      <section className="auth__main">
        <Confetti run={celebrate} />
        <motion.div className="auth__card auth__card--wide" layout>
          <AnimatePresence mode="wait">
            {celebrate ? (
              <motion.div key="yay" className="celebrate" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                <motion.div className="celebrate__heart" animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1 }}>
                  <Icon name="heart" size={48} fill="currentColor" stroke={0} />
                </motion.div>
                <h1>Welcome to CTshaadi, {form.firstName}!</h1>
                <p className="muted">Setting up your lounge with today's matches…</p>
              </motion.div>
            ) : method === null || method === 'medhire-form' ? (
              <motion.div key="method" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                <h1>Create your profile</h1>
                <p className="muted">Already a member? <Link to="/login">Login</Link></p>
                {method === 'medhire-form' ? (
                  <>
                    <MedhireForm onResult={onMedhire} cta="Import my MedHire profile" />
                    <button className="link-btn" onClick={() => setMethod(null)}><Icon name="chevronLeft" size={16} /> Other ways to register</button>
                  </>
                ) : (
                  <div className="method-cards">
                    <motion.button className="method method--medhire" whileHover={{ y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => setMethod('medhire-form')}>
                      <span className="method__icon"><Icon name="steth" size={26} /></span>
                      <strong>Continue with MedHire</strong>
                      <small>Auto-fill education, work, and personal details from your MedHire account.</small>
                      <span className="pill pill--teal">Fastest · ~1 min</span>
                    </motion.button>
                    <motion.button className="method" whileHover={{ y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => setMethod('email')}>
                      <span className="method__icon"><Icon name="mail" size={26} /></span>
                      <strong>Register with email</strong>
                      <small>Fill in your details step by step. Takes about 3 minutes.</small>
                      <span className="pill">Free forever</span>
                    </motion.button>
                  </div>
                )}
              </motion.div>
            ) : step === -1 ? (
              <motion.div key="imported" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
                <h1>Hello, {form.firstName || 'there'}! 👋</h1>
                <p className="muted">We found your MedHire profile and imported {imported.length} details. You'll review everything next.</p>
                <div className="imported">
                  {imported.map(([k, l], i) => (
                    <motion.div key={k} className="imported__row" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.07 }}>
                      <motion.span className="import-demo__tick" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.25 + i * 0.07, type: 'spring' }}>
                        <Icon name="check" size={13} stroke={3} />
                      </motion.span>
                      {l}
                    </motion.div>
                  ))}
                </div>
                {medhireSalary?.present || medhireSalary?.expected ? (
                  <p className="note"><Icon name="rupee" size={16} /> Your MedHire salary was found. On the career step you can show the same amount, a different one, or keep it private.</p>
                ) : null}
                <button className="btn btn--primary btn--lg" onClick={() => setStep(0)}>
                  Review my profile <Icon name="arrowRight" size={18} />
                </button>
              </motion.div>
            ) : (
              <motion.div key="wizard" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="stepper">
                  {STEPS.map((s, i) => (
                    <div key={s.key} className={`stepper__item ${i <= step ? 'is-done' : ''} ${i === step ? 'is-current' : ''}`}>
                      <span className="stepper__dot"><Icon name={i < step ? 'check' : s.icon} size={15} stroke={2.2} /></span>
                      <small>{s.title}</small>
                    </div>
                  ))}
                  <motion.div className="stepper__bar" animate={{ width: `${(step / (STEPS.length - 1)) * 100}%` }} />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={step} className="wizard__body" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.28 }}>
                    {step === 0 && (
                      <>
                        <h2>{STEPS[0].title}</h2>
                        <Field label="This profile is for">
                          <div className="chips">
                            {(o.CREATED_BY || ['Self']).map((c) => (
                              <button type="button" key={c} className={`chip ${form.createdBy === c ? 'is-active' : ''}`} onClick={() => set('createdBy')(c)}>
                                {c === 'Self' ? 'Myself' : `My ${c.toLowerCase()}`}
                              </button>
                            ))}
                          </div>
                        </Field>
                        <div className="row-2">
                          <Field label="First name" badge={tag('firstName')}>
                            <input className="input" value={form.firstName || ''} onChange={set('firstName')} placeholder="e.g. Dr. Ananya" />
                            {err('firstName')}
                          </Field>
                          <Field label="Last name" badge={tag('lastName')}>
                            <input className="input" value={form.lastName || ''} onChange={set('lastName')} />
                          </Field>
                        </div>
                        <Field label="Gender" badge={tag('gender')}>
                          <div className="seg seg--wide">
                            {['Female', 'Male'].map((g) => (
                              <button type="button" key={g} className={form.gender === g ? 'is-active' : ''} onClick={() => set('gender')(g)}>
                                {form.gender === g && <motion.span layoutId="g-seg" className="seg__pill" />}
                                <span>{g === 'Female' ? 'Woman' : 'Man'}</span>
                              </button>
                            ))}
                          </div>
                          {err('gender')}
                        </Field>
                        <div className="row-2">
                          <Field label="Date of birth" badge={tag('dob')}>
                            <input className="input" type="date" value={form.dob || ''} onChange={set('dob')} max={toDateInput(new Date())} />
                            {err('dob')}
                          </Field>
                          <Field label="Mobile number" badge={tag('phone')}>
                            <input className="input" value={form.phone || ''} onChange={set('phone')} placeholder="+91 98765 43210" />
                          </Field>
                        </div>
                        <div className="row-2">
                          <Field label="Email" badge={tag('email')}>
                            <input className="input" type="email" value={form.email || ''} onChange={set('email')} />
                            {err('email')}
                          </Field>
                          <Field label={ticket ? 'CTshaadi password (optional)' : 'Create password'} hint={ticket ? 'You can always login with MedHire instead.' : null}>
                            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 characters" autoComplete="new-password" />
                            {err('password')}
                          </Field>
                        </div>
                      </>
                    )}

                    {step === 1 && (
                      <>
                        <h2>{STEPS[1].title}</h2>
                        <div className="row-2">
                          <Field label="Height">
                            <Select value={form.heightCm} onChange={set('heightCm')} options={HEIGHTS.map((h) => ({ value: h.cm, label: h.label }))} />
                            {err('heightCm')}
                          </Field>
                          <Field label="Marital status" badge={tag('maritalStatus')}>
                            <Select value={form.maritalStatus} onChange={set('maritalStatus')} options={o.MARITAL || []} />
                          </Field>
                        </div>
                        <div className="row-2">
                          <Field label="Religion">
                            <Select value={form.religion} onChange={set('religion')} options={o.RELIGIONS || []} />
                            {err('religion')}
                          </Field>
                          <Field label="Community / caste" hint="Optional">
                            <input className="input" value={form.caste || ''} onChange={set('caste')} />
                          </Field>
                        </div>
                        <div className="row-2">
                          <Field label="Mother tongue" badge={tag('motherTongue')}>
                            <Select value={form.motherTongue} onChange={set('motherTongue')} options={withOption(o.MOTHER_TONGUES, form.motherTongue)} />
                            {err('motherTongue')}
                          </Field>
                          <Field label="Diet">
                            <Select value={form.diet} onChange={set('diet')} options={o.DIETS || []} />
                          </Field>
                        </div>
                        <div className="row-2">
                          <Field label="City you live in" badge={tag('city')}>
                            <input className="input" list="cities" value={form.city || ''} onChange={set('city')} />
                            <datalist id="cities">{(o.CITIES || []).map((c) => <option key={c} value={c} />)}</datalist>
                            {err('city')}
                          </Field>
                          <Field label="State" badge={tag('state')}>
                            <input className="input" value={form.state || ''} onChange={set('state')} />
                          </Field>
                        </div>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <h2>{STEPS[2].title}</h2>
                        <div className="row-2">
                          <Field label="Highest qualification" badge={tag('highestQualification')}>
                            <Select value={form.highestQualification} onChange={set('highestQualification')} options={withOption(o.QUALIFICATIONS, form.highestQualification)} />
                            {err('highestQualification')}
                          </Field>
                          <Field label="College / University" badge={tag('education')}>
                            <input className="input" value={form.education?.[0]?.institution || ''} onChange={setEdu('institution')} />
                          </Field>
                        </div>
                        <div className="row-2">
                          <Field label="Profession" badge={tag('profession')}>
                            <Select value={form.profession} onChange={set('profession')} options={withOption(o.PROFESSIONS, form.profession)} />
                            {err('profession')}
                          </Field>
                          <Field label="Specialty / field" badge={tag('specialty')}>
                            <input className="input" value={form.specialty || ''} onChange={set('specialty')} placeholder="e.g. Cardiology" />
                          </Field>
                        </div>
                        <div className="row-3">
                          <Field label="Designation" badge={tag('designation')}>
                            <input className="input" value={form.designation || ''} onChange={set('designation')} />
                          </Field>
                          <Field label="Hospital / employer" badge={tag('employer')}>
                            <input className="input" value={form.employer || ''} onChange={set('employer')} />
                          </Field>
                          <Field label="Experience (yrs)" badge={tag('experienceYears')}>
                            <input className="input" type="number" min="0" value={form.experienceYears ?? ''} onChange={set('experienceYears')} />
                          </Field>
                        </div>
                        {form.workHistory?.length > 0 && (
                          <div className="mini-timeline">
                            <small className="muted">Work history imported from MedHire</small>
                            {form.workHistory.slice(0, 4).map((w, i) => (
                              <div key={i} className="mini-timeline__row">
                                <span className="dot" />
                                <strong>{w.title}</strong> · {w.organization} <small className="muted">{w.from} – {w.to || 'Present'}</small>
                              </div>
                            ))}
                          </div>
                        )}
                        <h3 className="sub-h">Annual income</h3>
                        <IncomeChooser value={form.income} onChange={set('income')} medhireSalary={medhireSalary} />
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <h2>{STEPS[3].title}</h2>
                        <Field label="About me" badge={tag('about')} hint={`${(form.about || '').length}/1000 · Mention your work, values and what you're looking for`}>
                          <textarea className="input" rows={5} maxLength={1000} value={form.about || ''} onChange={set('about')} placeholder="Between ward rounds and night duties…" />
                        </Field>
                        <Field label="Hobbies & interests">
                          <ChipSelect options={o.HOBBIES || []} value={form.hobbies || []} onChange={set('hobbies')} anyLabel={null} />
                        </Field>
                        <Field label="Photos" hint="Profiles with photos get 8× more responses. JPG/PNG up to 5 MB.">
                          <div className="photo-picker">
                            {(form.photos || []).map((src) => (
                              <div key={src} className="photo-picker__item"><img src={src} alt="" /><span className="medhire-chip">MedHire</span></div>
                            ))}
                            {files.map((f, i) => (
                              <motion.div key={f.name + i} className="photo-picker__item" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                                <img src={URL.createObjectURL(f)} alt="" />
                                <button type="button" className="photo-picker__x" onClick={() => setFiles(files.filter((_, j) => j !== i))}><Icon name="x" size={14} /></button>
                              </motion.div>
                            ))}
                            <label className="photo-picker__add">
                              <Icon name="camera" size={24} />
                              <small>Add photo</small>
                              <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => setFiles([...files, ...Array.from(e.target.files)].slice(0, 6))} />
                            </label>
                          </div>
                        </Field>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="wizard__nav">
                  <button className="btn btn--ghost" onClick={() => (step === 0 ? (ticket ? setStep(-1) : setMethod(null)) : setStep(step - 1))}>
                    <Icon name="chevronLeft" size={17} /> Back
                  </button>
                  <motion.button className="btn btn--primary btn--lg" whileTap={{ scale: 0.96 }} onClick={next} disabled={busy}>
                    {busy ? 'Creating profile…' : step === STEPS.length - 1 ? 'Create my profile' : 'Continue'}
                    {!busy && <Icon name={step === STEPS.length - 1 ? 'heart' : 'arrowRight'} size={18} />}
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>
    </Page>
  );
}
